import { useEffect, useState, useRef, useCallback } from 'react';
import { SignedAmountInput } from '../components/SignedAmountInput';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import WorkflowStepper from '../components/WorkflowStepper';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { ReasonDialog } from '../components/ReasonDialog';
import { AdminBulkActions } from '../components/AdminBulkActions';
import { PODetailDialog } from '../components/po/PODetailDialog';
import { POSubmitToAPDialog } from '../components/po/POSubmitToAPDialog';
import { SearchableSelect } from '../components/SearchableSelect';
import { Pagination } from '../components/Pagination';
import ItemAutocomplete from '../components/ItemAutocomplete';
import { POBulkImportDialog } from '../components/po/POBulkImportDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import api from '../lib/api';
import { openPrintWindow, openPendingPrintWindow, printWhenReady, POPUP_BLOCKED_MESSAGE, escapeHtml, formatRupiah } from '../utils/printHelper';
import { findCompany, signatory, signatureBox, signatureRow, SIGNATURE_STYLE } from '../utils/signatures';
import { parseUnitPrice, formatUnitPriceInput, formatCurrencyID, parseQuantity, formatQuantityInput, formatQuantity } from '../utils/currency';
import { Plus, Eye, Send, Trash2, Calendar as CalendarIcon, RefreshCw, Pencil, Printer, ArrowUpDown, ArrowUp, ArrowDown, Package, FileText, PackageCheck, Undo2, Lock, Download } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';



import { POPrintOptionsDialog } from '../components/po/POPrintOptionsDialog';
import { PODownPaymentDialog } from '../components/po/PODownPaymentDialog';
import { POPartialDialog } from '../components/po/POPartialDialog';
import { POConfirmDialog } from '../components/po/POConfirmDialog';
import { POTable } from '../components/po/POTable';
import { POForm } from '../components/po/POForm';
import { ExportMenu } from '../components/ExportMenu';
// Helper function to update text to uppercase (except email)
const toUpperCaseExceptEmail = (field, value) => {
  if (field === 'email' || field === 'email_supplier') {
    return value.toLowerCase();
  }
  if (typeof value === 'string' && !field.includes('nomor') && !field.includes('quantity') && !field.includes('price')) {
    return value.toUpperCase();
  }
  return value;
};

export default function PurchaseOrders() {
  const { user } = useAuth();
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmType, setConfirmType] = useState(''); // diterima, diterima_adj, reject
  const [tanggalTerima, setTanggalTerima] = useState(null);
  const [alasanTidakSesuai, setAlasanTidakSesuai] = useState('');
  const [partialItems, setPartialItems] = useState([]); // For partial delivery checkbox
  const [showPartial, setShowPartial] = useState(false); // Partial delivery dialog
  const [showSubmitToAP, setShowSubmitToAP] = useState(false); // Submit to AP dialog
  const [showPrintOptions, setShowPrintOptions] = useState(false); // Print options dialog
  const [printPO, setPrintPO] = useState(null); // PO to print
  const [invoiceNumber, setInvoiceNumber] = useState(''); // Manual invoice number for submit to AP
  const [tanggalInvoice, setTanggalInvoice] = useState(null); // Manual invoice date
  const [submitUsesVA, setSubmitUsesVA] = useState(false); // VA checkbox in submit modal
  const [submitVAData, setSubmitVAData] = useState(null); // VA lookup data for submit modal
  const [submitPembulatan, setSubmitPembulatan] = useState(0); // Pembulatan for invoice submit
  const [submitBiayaAngkut, setSubmitBiayaAngkut] = useState(0); // Biaya angkut for invoice submit
  const [alasanReject, setAlasanReject] = useState(''); // Reason for reject
  const [sortField, setSortField] = useState('po_date'); // Default sort by date
  const [sortDirection, setSortDirection] = useState('desc'); // desc = newest first
  const [statusPriorityIndex, setStatusPriorityIndex] = useState(0); // Cycle index for status column
  const availableStatusesRef = useRef([]); // Distinct statuses in current dataset (ref to avoid re-fetch loops)
  const currentPageRef = useRef(1); // Preserve current page across submit/update actions
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cascadeChildren, setCascadeChildren] = useState(null);
  const [cascadeLoading, setCascadeLoading] = useState(false);
  const [reasonTarget, setReasonTarget] = useState(null);
  const [reasonLoading, setReasonLoading] = useState(false);
  const [poSearchFilter, setPoSearchFilter] = useState('');
  const [showDPDialog, setShowDPDialog] = useState(false);
  const [dpPO, setDpPO] = useState(null);
  const [dpAmount, setDpAmount] = useState('');
  const [dpLoading, setDpLoading] = useState(false);
  const printRef = useRef(null);
  
  const today = new Date().toISOString().split('T')[0];
  
  const initialFormState = {
    supplier_id: '',
    company_id: '',
    email_supplier: '',
    nomor_wa_supplier: '',
    alamat_pengiriman: [''],
    termin_pembayaran: 7,
    jenis_pengiriman: 'full',
    items: [{ name: '', satuan: 'pcs', quantity: 1, quantity_display: '1', unit_price: 0, unit_price_display: '', delivery_date: '', product_id: null, kode_barang: null }],
    has_ppn: false,
    ppn_percentage: 11,
    pembulatan: 0,
    biaya_angkut: 0,
    notes: '',
    uses_va: false
  };
  
  const [formData, setFormData] = useState(initialFormState);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(poSearchFilter.trim()), 400);
    return () => clearTimeout(t);
  }, [poSearchFilter]);

  const fetchPOs = useCallback(async (page) => {
    // When called without arg (after submit/update/delete), preserve current page.
    // Explicit page (e.g. from Pagination click or search/sort effect) always wins.
    const targetPage = typeof page === 'number' ? page : currentPageRef.current;
    try {
      const params = new URLSearchParams({ page: targetPage, limit: 20, sort_by: sortField, sort_dir: sortDirection });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const currentStatuses = availableStatusesRef.current;
      if (sortField === 'status' && currentStatuses.length > 0) {
        params.set('status_priority', currentStatuses[statusPriorityIndex % currentStatuses.length]);
      }
      const response = await api.get(`/purchase-orders?${params.toString()}`);
      const data = response.data;
      setPos(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total || 0);
      const newPage = data.page || 1;
      setCurrentPage(newPage);
      currentPageRef.current = newPage;
      availableStatusesRef.current = data.available_statuses || [];
    } catch (error) {
      console.error('Failed to fetch POs:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, sortField, sortDirection, statusPriorityIndex]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get(`/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await api.get(`/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, []);

  useEffect(() => {
    fetchPOs(1);
  }, [fetchPOs]);

  useEffect(() => {
    if (['purchasing', 'staff_clerk', 'account_payable', 'admin'].includes(user?.role)) fetchSuppliers();
    fetchCompanies();
  }, [fetchSuppliers, fetchCompanies, user?.role]);

  // Sorting function (server-side)
  const handleSort = (field) => {
    if (field === 'status') {
      // Cycle through statuses present in current dataset on each click
      const cycleLen = availableStatusesRef.current.length || 1;
      if (sortField === 'status') {
        setStatusPriorityIndex((prev) => (prev + 1) % cycleLen);
      } else {
        setSortField('status');
        setStatusPriorityIndex(0);
        setSortDirection('asc');
      }
      return;
    }
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 text-slate-400" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-4 w-4" style={{ color: '#b8960a' }} /> : 
      <ArrowDown className="h-4 w-4" style={{ color: '#b8960a' }} />;
  };

  // Open partial delivery dialog
  const openPartialDialog = (po) => {
    setSelectedPO(po);
    // Build map of existing received state by line_id for reliable per-item lookup
    const existingByLid = {};
    (po.received_items || []).forEach((ri, idx) => {
      const key = ri.line_id || `idx_${idx}`;
      existingByLid[key] = ri;
    });
    // Initialize partial items with received status and original prices.
    // A line is "locked" (TTB) when previously received_qty >= PO qty.
    const items = (po.items || []).map((item, idx) => {
      const lookupKey = item.line_id || `idx_${idx}`;
      const existing = existingByLid[lookupKey] || {};
      const wasReceived = !!existing.received;
      const wasQty = existing.received_qty ?? item.quantity;
      const wasPrice = existing.unit_price ?? existing.received_price ?? item.unit_price;
      const locked = wasReceived && wasQty >= item.quantity && item.quantity > 0;
      return {
        ...item,
        index: idx,
        original_qty: item.quantity,
        original_price: item.unit_price,
        received: wasReceived || false,
        received_qty: wasQty,
        previous_received_qty: wasQty, // Track previous qty to calculate delta
        received_price: wasPrice,
        locked,
      };
    });
    setPartialItems(items);
    setTanggalTerima(po.tanggal_terima ? new Date(po.tanggal_terima) : new Date());
    setShowPartial(true);
  };

  // Handle partial item checkbox change (blocked for locked lines)
  const handlePartialItemChange = (index, field, value) => {
    const newItems = [...partialItems];
    if (newItems[index].locked) return; // No-op — TTB locked
    newItems[index][field] = value;
    setPartialItems(newItems);
  };

  // Check if all items are received (for auto-complete)
  const allItemsReceived = () => {
    return partialItems.length > 0 && partialItems.every(item => item.received);
  };

  // Check if any price or qty changed from original
  const hasAdjustments = () => {
    return partialItems.some(item => 
      item.received && (
        item.received_qty !== item.original_qty ||
        item.received_price !== item.original_price
      )
    );
  };

  // Confirm partial delivery
  const handleConfirmPartial = async () => {
    if (!tanggalTerima) {
      toast.error('Pilih tanggal terima barang');
      return;
    }

    const receivedItems = partialItems.filter(item => item.received);
    if (receivedItems.length === 0) {
      toast.error('Pilih minimal satu item yang diterima');
      return;
    }

    // TTB dicetak setelah server menyimpan. Jendelanya dibuka SEKARANG (masih
    // dalam klik user) karena window.open() setelah await bisa diblokir.
    // Kondisi sama persis dengan filter `freshlyReceived` di bawah:
    // qty cetak = delta bila > 0, selain itu qty saat ini.
    const willPrintTtb = receivedItems.some(item => {
      const currentQty = parseFloat(item.received_qty) || 0;
      const delta = currentQty - (parseFloat(item.previous_received_qty) || 0);
      return (delta > 0 ? delta : currentQty) > 0;
    });
    const ttbWindow = willPrintTtb ? openPendingPrintWindow('Menyiapkan Tanda Terima Barang…') : null;

    try {
      // Determine status: if all items received, mark as complete
      let status = 'diterima_partial';
      if (allItemsReceived()) {
        status = hasAdjustments() ? 'diterima_adj' : 'diterima';
      }

      const response = await api.patch(`/purchase-orders/${selectedPO.id}/confirm`, {
        status: status,
        tanggal_terima: format(tanggalTerima, 'yyyy-MM-dd'),
        received_items: partialItems.map(item => ({
          ...item,
          unit_price: item.received_price || item.unit_price
        })),
        // For diterima_adj, also send updated items
        items: status === 'diterima_adj' ? receivedItems.map(item => ({
          line_id: item.line_id,
          product_id: item.product_id,
          name: item.name,
          satuan: item.satuan,
          quantity: item.received_qty,
          unit_price: item.received_price || item.unit_price,
          delivery_date: item.delivery_date
        })) : undefined,
        alasan_tidak_sesuai: hasAdjustments() ? 'QTY/HARGA TIDAK SESUAI PO' : undefined
      });
      
      if (status === 'diterima') {
        toast.success('Semua barang diterima sesuai PO');
      } else if (status === 'diterima_adj') {
        toast.success('Semua barang diterima dengan adjustment');
      } else {
        toast.success('Partial delivery berhasil disimpan');
      }
      
      // Print TTB for newly received items (delta > 0)
      if (receivedItems.length > 0) {
        const freshlyReceived = receivedItems.map(item => {
          const currentQty = parseFloat(item.received_qty) || 0;
          const prevQty = parseFloat(item.previous_received_qty) || 0;
          const delta = currentQty - prevQty;
          return {
            ...item,
            quantity: delta > 0 ? delta : currentQty, // Fallback to current if no prev
            unit_price: item.received_price || item.unit_price
          };
        }).filter(item => item.quantity > 0);
        
        if (freshlyReceived.length > 0) {
          const ttbData = { 
            ...response.data, 
            items: freshlyReceived
          };
          printTandaTerimaPartial(ttbData, ttbWindow);
        } else if (ttbWindow) {
          ttbWindow.close();
        }
      } else if (ttbWindow) {
        ttbWindow.close();
      }
      
      setShowPartial(false);
      fetchPOs();
    } catch (error) {
      if (ttbWindow) ttbWindow.close();
      toast.error(error.response?.data?.detail || 'Gagal menyimpan partial delivery');
    }
  };

  // Submit partial to AP (create invoice)
  const handleSubmitToAP = async () => {
    if (!invoiceNumber.trim()) {
      toast.error('Masukkan nomor invoice');
      return;
    }

    if (!tanggalTerima) {
      toast.error('Pilih tanggal terima barang');
      return;
    }

    if (!tanggalInvoice) {
      toast.error('Pilih tanggal invoice');
      return;
    }

    // Only rows that are checked, not already fully invoiced, received by the
    // warehouse, and with remaining qty.
    const itemsToSubmit = partialItems.filter(
      (item) => item.received && !item.fully_invoiced && !item.belum_diterima
        && ((item.remaining_qty ?? item.quantity) > 0)
    );
    if (itemsToSubmit.length === 0) {
      toast.error('Tidak ada item yang tersedia untuk disubmit ke AP');
      return;
    }

    try {
      const response = await api.post(`/purchase-orders/${selectedPO.id}/submit-to-ap`, {
        invoice_number: invoiceNumber.trim().toUpperCase(),
        tanggal_terima: format(tanggalTerima, 'yyyy-MM-dd'),
        tanggal_invoice: format(tanggalInvoice, 'yyyy-MM-dd'),
        items_to_submit: itemsToSubmit.map((item) => ({
          line_id: item.line_id,
          name: item.name,
          satuan: item.satuan,
          quantity: item.received_qty || item.quantity,
          unit_price: item.unit_price,
          delivery_date: item.delivery_date,
        })),
        uses_va: submitUsesVA,
        pembulatan: submitPembulatan || 0,
        biaya_angkut: submitBiayaAngkut || 0
      });

      toast.success(`Invoice ${response.data.invoice_number} berhasil dibuat dan dikirim ke AP`);
      setShowSubmitToAP(false);
      setShowPartial(false);
      setInvoiceNumber('');
      setTanggalInvoice(null);
      setSubmitUsesVA(false);
      setSubmitVAData(null);
      setSubmitPembulatan(0);
      setSubmitBiayaAngkut(0);
      // Preserve current page by using fetchPOs default (currentPageRef)
      fetchPOs();
    } catch (error) {
      // Surface backend-specific detail if present
      const detail = error?.response?.data?.detail;
      toast.error(detail ? `Gagal submit ke AP: ${detail}` : 'Gagal submit ke AP');
    }
  };

  // Print Tanda Terima for partial items
  const printTandaTerimaPartial = (po, targetWindow = null) => {
    const pageHeight = 99; // 1/3 A4 in mm ≈ 99mm
    const systemDate = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    
    const printWindow = openPrintWindow(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tanda Terima Barang - ${po.po_number}</title>
        <style>
          @page { size: A4; margin: 0; }
          body { font-family: Calibri, 'Segoe UI', sans-serif; padding: 10px; text-transform: uppercase; font-size: 9pt; margin: 0; height: ${pageHeight}mm; box-sizing: border-box; }
          .header { text-align: center; margin-bottom: 8px; border-bottom: 2px solid #16a34a; padding-bottom: 8px; }
          .header h1 { margin: 0; color: #16a34a; font-size: 14pt; }
          .header h2 { margin: 4px 0 0 0; font-size: 11pt; }
          .info { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 9pt; }
          .info p { margin: 2px 0; }
          .badge { display: inline-block; background: #fef3c7; color: #d97706; padding: 2px 8px; border-radius: 4px; font-size: 8pt; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 9pt; }
          th, td { border: 1px solid #d1d5db; padding: 4px 6px; text-align: left; }
          th { background: #f0fdf4; font-size: 9pt; }
          .total { background: #f0fdf4; padding: 6px; margin-bottom: 8px; text-align: right; font-weight: bold; }
          .footer { text-align: right; font-size: 8pt; color: #6b7280; position: absolute; bottom: 10px; right: 10px; }
          .lowercase { text-transform: lowercase; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>TANDA TERIMA BARANG (PARTIAL)</h1>
          <h2>${(po.company_name || '').toUpperCase()}</h2>
        </div>
        <div class="info">
          <div>
            <p><strong>NO. PO:</strong> ${po.po_number}</p>
            <p><strong>SUPPLIER:</strong> ${po.supplier_name}</p>
            <p><strong>TGL TERIMA:</strong> ${po.tanggal_terima ? new Date(po.tanggal_terima).toLocaleDateString('id-ID') : '-'}</p>
          </div>
          <div style="text-align: right;">
            <span class="badge">PARTIAL DELIVERY</span>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 30px;">NO</th>
              <th>NAMA ITEM</th>
              <th style="width: 70px;">SATUAN</th>
              <th style="width: 90px;">QTY DITERIMA</th>
            </tr>
          </thead>
          <tbody>
            ${(po.items || []).map((item, i) => `
              <tr>
                <td style="text-align: center;">${i + 1}</td>
                <td>${item.name}</td>
                <td style="text-align: center;">${(item.satuan || 'pcs').toUpperCase()}</td>
                <td style="text-align: center;">${(item.received_qty ?? item.quantity ?? 0).toLocaleString('id-ID')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ${signatureRow([
          signatureBox('DISERAHKAN OLEH', 'PENGANTAR SUPPLIER'),
          signatureBox('DITERIMA OLEH', 'GUDANG'),
        ])}
        <div class="footer">
          Tanggal Cetak: ${systemDate}
        </div>
      </body>
      </html>
    `, 'Print', targetWindow);
    if (!printWindow) {
      toast.error(POPUP_BLOCKED_MESSAGE);
      return;
    }
    printWhenReady(printWindow);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { name: '', satuan: 'pcs', quantity: 1, quantity_display: '1', unit_price: 0, unit_price_display: '', delivery_date: '', product_id: null, kode_barang: null }]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  // Memilih dari katalog mengikat item ke produknya. Nama tetap disimpan
  // sebagai snapshot untuk cetakan lama; identitasnya ada di product_id.
  const handleItemSelectProduct = (index, product) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      name: (product.nama_barang || '').toUpperCase(),
      satuan: product.satuan || newItems[index].satuan || 'pcs',
      product_id: product.id,
      kode_barang: product.kode_barang || null,
    };
    setFormData({ ...formData, items: newItems });
  };

  // Item baru wajib berasal dari Daftar Barang. Baris yang sudah ada di PO
  // lama dikecualikan lewat catalog_exempt supaya tetap bisa diedit.
  const itemsOutsideCatalog = (items) =>
    items.filter((item) => !item.product_id && !item.catalog_exempt);

  const blockIfOutsideCatalog = () => {
    const offenders = itemsOutsideCatalog(formData.items);
    if (offenders.length === 0) return false;
    const names = offenders.map((i) => i.name || '(kosong)').join(', ');
    toast.error(
      `Item berikut belum ada di Daftar Barang: ${names}. ` +
      'Pilih dari saran yang muncul, atau daftarkan dulu di menu Daftar Barang.'
    );
    return true;
  };

  const mapItemsForSubmit = (items) =>
    items.map((item) => ({
      ...(item.line_id ? { line_id: item.line_id } : {}),
      name: item.name,
      satuan: item.satuan || 'pcs',
      quantity: item.quantity,
      unit_price: item.unit_price,
      delivery_date: item.delivery_date,
      product_id: item.product_id || null,
      kode_barang: item.kode_barang || null,
    }));

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === 'name') {
      // Convert name to uppercase
      newItems[index][field] = value.toUpperCase();
      // Diketik manual: item tidak lagi terikat ke produk katalog.
      newItems[index].product_id = null;
      newItems[index].kode_barang = null;
    } else if (field === 'delivery_date' || field === 'satuan') {
      newItems[index][field] = value;
    } else if (field === 'quantity') {
      // Same display-string pattern as unit_price, so the thousand separators
      // survive while the user is still typing.
      const displayValue = formatQuantityInput(value);
      newItems[index].quantity = Math.max(0.01, parseQuantity(displayValue));
      newItems[index].quantity_display = displayValue;
    } else if (field === 'unit_price') {
      // Handle unit price with thousand separator + Indonesian decimal (comma)
      const displayValue = formatUnitPriceInput(value);
      newItems[index].unit_price = parseUnitPrice(displayValue);
      newItems[index].unit_price_display = displayValue;
    }
    setFormData({ ...formData, items: newItems });
  };

  const handleAddAddress = () => {
    setFormData({
      ...formData,
      alamat_pengiriman: [...formData.alamat_pengiriman, '']
    });
  };

  const handleRemoveAddress = (index) => {
    const newAddresses = formData.alamat_pengiriman.filter((_, i) => i !== index);
    setFormData({ ...formData, alamat_pengiriman: newAddresses });
  };

  const handleAddressChange = (index, value) => {
    const newAddresses = [...formData.alamat_pengiriman];
    newAddresses[index] = value.toUpperCase();
    setFormData({ ...formData, alamat_pengiriman: newAddresses });
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setFormData(prev => ({ 
      ...prev, 
      supplier_id: supplierId,
      email_supplier: supplier?.email?.toLowerCase() || '',
      nomor_wa_supplier: supplier?.nomor_wa || '',
      uses_va: supplier?.uses_virtual_account || false
    }));
  };

  // Helper to update form field with uppercase except email
  const updatePOFormField = (field, value) => {
    if (field === 'email_supplier') {
      setFormData({ ...formData, [field]: value.toLowerCase() });
    } else if (field === 'notes') {
      setFormData({ ...formData, [field]: value.toUpperCase() });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_id) {
      toast.error('Pilih supplier terlebih dahulu');
      return;
    }
    if (!formData.company_id) {
      toast.error('Pilih perusahaan tujuan terlebih dahulu');
      return;
    }
    
    const validAddresses = formData.alamat_pengiriman.filter(addr => addr.trim() !== '');
    if (validAddresses.length === 0) {
      toast.error('Masukkan minimal satu alamat pengiriman');
      return;
    }
    if (blockIfOutsideCatalog()) return;

    try {
      const submitData = {
        ...formData,
        alamat_pengiriman: validAddresses,
        items: mapItemsForSubmit(formData.items)
      };
      delete submitData.unit_price_display;
      submitData.pembulatan = parseFloat(formData.pembulatan) || 0;
      submitData.biaya_angkut = parseFloat(formData.biaya_angkut) || 0;
      await api.post(`/purchase-orders`, submitData);
      toast.success('Purchase order berhasil dibuat!');
      setShowCreate(false);
      resetForm();
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat purchase order');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    if (!selectedPO) return;
    
    const validAddresses = formData.alamat_pengiriman.filter(addr => addr.trim() !== '');
    if (validAddresses.length === 0) {
      toast.error('Masukkan minimal satu alamat pengiriman');
      return;
    }
    if (blockIfOutsideCatalog()) return;

    try {
      const submitData = {
        ...formData,
        alamat_pengiriman: validAddresses,
        items: mapItemsForSubmit(formData.items)
      };
      delete submitData.unit_price_display;
      submitData.pembulatan = parseFloat(formData.pembulatan) || 0;
      submitData.biaya_angkut = parseFloat(formData.biaya_angkut) || 0;
      await api.put(`/purchase-orders/${selectedPO.id}`, submitData);
      toast.success('Purchase order berhasil diupdate!');
      setShowEdit(false);
      resetForm();
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal update purchase order');
    }
  };

  const openEdit = (po) => {
    setSelectedPO(po);
    setFormData({
      supplier_id: po.supplier_id || '',
      company_id: po.company_id || '',
      email_supplier: po.email_supplier || po.supplier_data?.email || '',
      nomor_wa_supplier: po.nomor_wa_supplier || po.supplier_data?.nomor_wa || '',
      alamat_pengiriman: po.alamat_pengiriman || [''],
      termin_pembayaran: po.termin_pembayaran || 7,
      jenis_pengiriman: po.jenis_pengiriman || 'full',
      items: (po.items || []).map(item => ({
        name: item.name || '',
        satuan: item.satuan || 'pcs',
        quantity: item.quantity || 1,
        quantity_display: formatQuantity(item.quantity || 1),
        unit_price: item.unit_price || 0,
        unit_price_display: formatCurrencyID(item.unit_price || 0),
        delivery_date: item.delivery_date || '',
        line_id: item.line_id,
        product_id: item.product_id || null,
        kode_barang: item.kode_barang || null,
        // Baris yang sudah ada di PO ini dibuat sebelum katalog diwajibkan —
        // jangan halangi pengeditan PO lama karenanya.
        catalog_exempt: true,
      })),
      has_ppn: po.has_ppn || false,
      ppn_percentage: po.ppn_percentage || 11,
      pembulatan: po.pembulatan || 0,
      biaya_angkut: po.biaya_angkut || 0,
      notes: po.notes || ''
    });
    setShowEdit(true);
  };

  const handleSubmit = async (poId) => {
    try {
      await api.patch(`/purchase-orders/${poId}/submit`);
      toast.success('Purchase order berhasil disubmit!');
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal submit purchase order');
    }
  };

  // Admin delete function
  const openDeleteDialog = async (po) => {
    setItemToDelete(po);
    setCascadeChildren(null);
    setDeleteDialogOpen(true);
    setCascadeLoading(true);
    try {
      const r = await api.get(`/admin/transactions/purchase_order/${po.id}/cascade-preview`);
      setCascadeChildren(r.data.children || []);
    } catch (e) {
      setCascadeChildren([]);
    } finally {
      setCascadeLoading(false);
    }
  };

  // Batalkan / Tutup / Revisi PO
  const canManagePO = (po) => user?.role === 'admin' || (user?.role === 'purchasing' && po.created_by === user?.id);

  const handleReasonConfirm = async (reason) => {
    if (!reasonTarget) return;
    setReasonLoading(true);
    try {
      const path = reasonTarget.type === 'cancel' ? 'cancel' : 'close';
      await api.patch(`/purchase-orders/${reasonTarget.po.id}/${path}`, { alasan: reason });
      toast.success(reasonTarget.type === 'cancel'
        ? `PO ${reasonTarget.po.po_number} dibatalkan`
        : `PO ${reasonTarget.po.po_number} ditutup`);
      setReasonTarget(null);
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memproses PO');
    } finally {
      setReasonLoading(false);
    }
  };

  const handleRevise = async (po) => {
    try {
      const r = await api.post(`/purchase-orders/${po.id}/revise`);
      toast.success(`Draft revisi dibuat: ${r.data.po_number}`);
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat revisi PO');
    }
  };

  // Down Payment handler
  const handleCreateDP = async () => {
    if (!dpPO || !dpAmount) return;
    setDpLoading(true);
    try {
      await api.post(`/down-payments`, {
        po_id: dpPO.id,
        amount: parseFloat(dpAmount)
      });
      toast.success(`DP berhasil dibuat: DP-${dpPO.po_number}`);
      setShowDPDialog(false);
      setDpPO(null);
      setDpAmount('');
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat DP');
    } finally {
      setDpLoading(false);
    }
  };


  const handleDelete = async (reason) => {
    if (!itemToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/admin/transactions/purchase_order/${itemToDelete.id}`, {
        data: { reason }
      });
      toast.success(`PO ${itemToDelete.po_number} berhasil dihapus`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus PO');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Gudang confirmation functions
  const openConfirmDialog = (po, type) => {
    setSelectedPO(po);
    setConfirmType(type);
    setTanggalTerima(new Date());
    setAlasanTidakSesuai('');
    if (type === 'diterima_adj') {
      setFormData({
        ...formData,
        items: (po.items || []).map(item => ({
          line_id: item.line_id,
          product_id: item.product_id,
          name: item.name || '',
          satuan: item.satuan || 'pcs',
          quantity: item.quantity || 1,
          quantity_display: formatQuantity(item.quantity || 1),
          unit_price: item.unit_price || 0,
          unit_price_display: formatCurrencyID(item.unit_price || 0),
          delivery_date: item.delivery_date || ''
        }))
      });
    }
    setShowConfirm(true);
  };

  const handleConfirmPO = async () => {
    // Only require tanggalTerima for non-reject confirmations
    if (confirmType !== 'reject' && !tanggalTerima) {
      toast.error('Pilih tanggal terima barang');
      return;
    }

    // Require alasan for reject
    if (confirmType === 'reject' && !alasanReject.trim()) {
      toast.error('Masukkan alasan reject');
      return;
    }
    
    let ttbWindow = null;
    try {
      const confirmData = {
        status: confirmType,
        tanggal_terima: confirmType === 'reject' ? new Date().toISOString().split('T')[0] : format(tanggalTerima, 'yyyy-MM-dd')
      };
      
      if (confirmType === 'diterima_adj') {
        if (!alasanTidakSesuai) {
          toast.error('Masukkan alasan tidak sesuai');
          return;
        }
        confirmData.alasan_tidak_sesuai = alasanTidakSesuai;
        confirmData.items = formData.items.map(item => ({
          line_id: item.line_id,
          product_id: item.product_id,
          name: item.name,
          satuan: item.satuan,
          quantity: item.quantity,
          unit_price: item.unit_price,
          delivery_date: item.delivery_date
        }));
      }

      if (confirmType === 'reject') {
        confirmData.alasan_reject = alasanReject.trim().toUpperCase();
      }
      
      // TTB: jendela dibuka sebelum await (masih dalam klik user), diisi
      // setelah server menyimpan. Reject tidak mencetak apa pun.
      ttbWindow = confirmType !== 'reject' ? openPendingPrintWindow('Menyiapkan Tanda Terima Barang…') : null;
      const response = await api.patch(`/purchase-orders/${selectedPO.id}/confirm`, confirmData);
      
      if (confirmType === 'reject') {
        toast.success('PO berhasil di-reject');
        setAlasanReject('');
      } else {
        toast.success('Konfirmasi berhasil! Mencetak Tanda Terima Barang...');
        printTandaTerima(response.data, ttbWindow);
      }
      
      setShowConfirm(false);
      fetchPOs();
    } catch (error) {
      if (ttbWindow) ttbWindow.close();
      toast.error(error.response?.data?.detail || 'Gagal konfirmasi PO');
    }
  };

  // Print Tanda Terima Barang (1/3 A4)
  const printTandaTerima = (po, targetWindow = null) => {
    const now = new Date();
    const tanggalSistem = format(now, 'dd MMMM yyyy, HH:mm', { locale: id });
    
    const printWindow = openPrintWindow(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>TTB-${po.po_number}</title>
        <style>
          @page { size: A4; margin: 5mm; }
          body { 
            font-family: Calibri, 'Segoe UI', Tahoma, sans-serif; 
            padding: 10px; 
            text-transform: uppercase; 
            font-size: 9pt;
            width: 100%;
            max-width: 210mm;
            min-height: 99mm; /* 1/3 of A4 height (297mm / 3) */
            max-height: 99mm;
            box-sizing: border-box;
            overflow: hidden;
          }
          .header { text-align: center; margin-bottom: 10px; border-bottom: 2px solid #1e40af; padding-bottom: 8px; }
          .header h1 { margin: 0; color: #1e40af; font-size: 14pt; font-weight: bold; }
          .header h2 { margin: 5px 0 0 0; color: #334155; font-size: 12pt; font-weight: bold; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .info-row div { width: 48%; }
          .info-row p { margin: 2px 0; font-size: 9pt; }
          .info-row strong { font-size: 10pt; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 9pt; }
          th { border: 1px solid #e2e8f0; padding: 4px 6px; text-align: left; background: #f1f5f9; font-size: 9pt; }
          td { border: 1px solid #e2e8f0; padding: 4px 6px; font-size: 9pt; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .total-section { background: #eff6ff; padding: 8px; border-radius: 4px; margin-bottom: 8px; }
          .total-row { display: flex; justify-content: space-between; font-size: 9pt; }
          .total-final { font-size: 11pt; font-weight: bold; color: #1e40af; }
          ${SIGNATURE_STYLE}
          .system-date { text-align: right; font-size: 8pt; color: #64748b; margin-top: 10px; text-transform: none; }
          .lowercase { text-transform: lowercase; }
          .normal-case { text-transform: none; }
          .reason { background: #fef3c7; padding: 6px; border-radius: 4px; margin-bottom: 8px; font-size: 9pt; }
          @media print { 
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } 
            @page { size: A4 portrait; margin: 5mm; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>TANDA TERIMA BARANG</h1>
          <h2>${(po.company_name || '-').toUpperCase()}</h2>
        </div>
        
        <div class="info-row">
          <div>
            <p><strong>NO. PO:</strong> ${po.po_number}</p>
            <p><strong>TGL TERIMA:</strong> ${po.tanggal_terima ? new Date(po.tanggal_terima).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase() : '-'}</p>
          </div>
          <div>
            <p><strong>SUPPLIER:</strong> ${(po.supplier_name || '').toUpperCase()}</p>
            <p><strong>STATUS:</strong> ${po.status === 'diterima' ? 'DITERIMA - SESUAI PO' : 'DITERIMA - ADJUSTMENT'}</p>
          </div>
        </div>
        
        ${po.alasan_tidak_sesuai ? `
          <div class="reason">
            <strong>ALASAN TIDAK SESUAI:</strong> ${po.alasan_tidak_sesuai.toUpperCase()}
          </div>
        ` : ''}
        
        <table>
          <thead>
            <tr>
              <th class="text-center" style="width: 30px;">NO</th>
              <th>NAMA ITEM</th>
              <th class="text-center" style="width: 70px;">SATUAN</th>
              <th class="text-center" style="width: 90px;">QTY DITERIMA</th>
            </tr>
          </thead>
          <tbody>
            ${(po.items || []).map((item, i) => `
              <tr>
                <td class="text-center">${i + 1}</td>
                <td>${(item.name || '').toUpperCase()}</td>
                <td class="text-center">${(item.satuan || 'PCS').toUpperCase()}</td>
                <td class="text-center normal-case">${(item.received_qty ?? item.quantity ?? 0).toLocaleString('id-ID')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        
        ${signatureRow([
          signatureBox('DISERAHKAN OLEH', 'PENGANTAR SUPPLIER'),
          signatureBox('DITERIMA OLEH', 'GUDANG'),
        ])}
        
        <div class="system-date">
          Dicetak: ${tanggalSistem}
        </div>
      </body>
      </html>
    `, 'Print', targetWindow);
    if (!printWindow) {
      toast.error(POPUP_BLOCKED_MESSAGE);
      return;
    }
    printWhenReady(printWindow);
  };

  const handlePrint = (po) => {
    // Penanda tangan dibaca dari master perusahaan, bukan dari salinan beku
    // po.company_data — pergantian pejabat langsung berlaku, termasuk saat
    // PO lama dicetak ulang.
    const poCompany = findCompany(companies, po.company_id);
    const poFat = signatory(poCompany, 'fat');
    const poGm = signatory(poCompany, 'gm');
    const printWindow = openPrintWindow(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${po.po_number}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Calibri:wght@400;700&display=swap');
          body { font-family: Calibri, 'Segoe UI', Tahoma, sans-serif; padding: 20px; text-transform: uppercase; font-size: 10pt; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #1e40af; padding-bottom: 15px; }
          .header h1 { margin: 0; color: #1e40af; font-size: 16pt; font-weight: bold; }
          .header h2 { margin: 8px 0 0 0; color: #334155; font-size: 14pt; font-weight: bold; }
          .po-info { display: flex; justify-content: space-between; margin-bottom: 15px; }
          .po-info div { width: 48%; }
          .po-info p { margin: 4px 0; font-size: 10pt; }
          .po-info strong { font-size: 11pt; }
          .section { margin-bottom: 15px; }
          .section-title { font-weight: bold; background: #f1f5f9; padding: 6px 8px; margin-bottom: 8px; font-size: 11pt; }
          .section p { font-size: 10pt; margin: 3px 0; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          th { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; background: #f1f5f9; font-size: 11pt; font-weight: bold; }
          td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; font-size: 10pt; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .total-section { background: #eff6ff; padding: 12px; border-radius: 6px; }
          .total-row { display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10pt; }
          .total-row strong { font-size: 11pt; }
          .total-final { font-size: 12pt; font-weight: bold; color: #1e40af; border-top: 2px solid #1e40af; padding-top: 8px; margin-top: 8px; }
          ${SIGNATURE_STYLE}
          .lowercase { text-transform: lowercase; }
          .normal-case { text-transform: none; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>PURCHASE ORDER</h1>
          <h2>${(po.company_name || '-').toUpperCase()}</h2>
        </div>
        
        <div class="po-info">
          <div>
            <p><strong>NO. PO:</strong> ${po.po_number}</p>
            <p><strong>TANGGAL:</strong> ${new Date(po.po_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase()}</p>
            <p><strong>TERMIN:</strong> ${po.termin_pembayaran || '-'} HARI</p>
            <p><strong>JENIS PENGIRIMAN:</strong> ${po.jenis_pengiriman === 'full' ? 'FULL (SEKALIGUS)' : 'PARTIAL (BERTAHAP)'}</p>
          </div>
          <div>
            <p><strong>SUPPLIER:</strong> ${(po.supplier_name || '').toUpperCase()}</p>
            <p><strong>EMAIL:</strong> <span class="lowercase">${po.email_supplier || po.supplier_data?.email || '-'}</span></p>
            <p><strong>NO. WA:</strong> ${po.nomor_wa_supplier || po.supplier_data?.nomor_wa || '-'}</p>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">ALAMAT PENGIRIMAN</div>
          ${(po.alamat_pengiriman || []).map((addr, i) => `<p>${i + 1}. ${addr.toUpperCase()}</p>`).join('')}
        </div>
        
        <div class="section">
          <div class="section-title">DAFTAR ITEMS</div>
          <table>
            <thead>
              <tr>
                <th class="text-center" style="width: 40px;">NO</th>
                <th>NAMA ITEM</th>
                <th class="text-center" style="width: 70px;">SATUAN</th>
                <th class="text-center" style="width: 80px;">QTY</th>
                <th class="text-right" style="width: 120px;">HARGA SATUAN</th>
                <th class="text-right" style="width: 120px;">JUMLAH</th>
                ${po.jenis_pengiriman === 'partial' ? '<th style="width: 100px;">TGL KIRIM</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${(po.items || []).map((item, i) => `
                <tr>
                  <td class="text-center">${i + 1}</td>
                  <td>${(item.name || '').toUpperCase()}</td>
                  <td class="text-center">${(item.satuan || 'PCS').toUpperCase()}</td>
                  <td class="text-center normal-case">${(item.quantity || 0).toLocaleString('id-ID')}</td>
                  <td class="text-right normal-case">Rp ${(item.unit_price || 0).toLocaleString('id-ID')}</td>
                  <td class="text-right normal-case">Rp ${((item.quantity || 0) * (item.unit_price || 0)).toLocaleString('id-ID')}</td>
                  ${po.jenis_pengiriman === 'partial' ? `<td class="text-center">${item.delivery_date ? new Date(item.delivery_date).toLocaleDateString('id-ID') : '-'}</td>` : ''}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="total-section">
          <div class="total-row">
            <strong>SUBTOTAL</strong>
            <span class="normal-case">Rp ${(po.subtotal || po.total_amount).toLocaleString('id-ID')}</span>
          </div>
          ${po.has_ppn ? `
            <div class="total-row">
              <strong>PPN (${po.ppn_percentage}%)</strong>
              <span class="normal-case">Rp ${(po.ppn_amount || 0).toLocaleString('id-ID')}</span>
            </div>
          ` : ''}
          ${po.biaya_angkut && po.biaya_angkut !== 0 ? `
            <div class="total-row">
              <strong>BIAYA ANGKUT</strong>
              <span class="normal-case">Rp ${po.biaya_angkut.toLocaleString('id-ID')}</span>
            </div>
          ` : ''}
          ${po.pembulatan && po.pembulatan !== 0 ? `
            <div class="total-row">
              <strong>PEMBULATAN</strong>
              <span class="normal-case">Rp ${po.pembulatan.toLocaleString('id-ID')}</span>
            </div>
          ` : ''}
          <div class="total-row total-final">
            <span>TOTAL PO</span>
            <span class="normal-case">Rp ${(po.total_amount || 0).toLocaleString('id-ID')}</span>
          </div>
        </div>
        
        ${po.notes ? `
          <div class="section">
            <div class="section-title">CATATAN</div>
            <p>${po.notes.toUpperCase()}</p>
          </div>
        ` : ''}
        
        ${signatureRow([
          signatureBox('DIBUAT OLEH', (po.created_by_name || '').toUpperCase()),
          signatureBox('DIKETAHUI OLEH', poFat.nama, poFat.jabatan),
          signatureBox('DISETUJUI OLEH', poGm.nama, poGm.jabatan),
        ])}
      </body>
      </html>
    `);
    if (!printWindow) {
      toast.error(POPUP_BLOCKED_MESSAGE);
      return;
    }
    printWhenReady(printWindow);
  };

  // Calculations
  const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  // Pembulatan corrects the total, not the tax base - see utils/invoiceAmounts.js
  const pembulatan = parseFloat(formData.pembulatan) || 0;
  const ppnAmount = formData.has_ppn ? subtotal * (formData.ppn_percentage / 100) : 0;
  const biayaAngkut = parseFloat(formData.biaya_angkut) || 0;
  const totalAmount = subtotal + ppnAmount + biayaAngkut + pembulatan;

  // Form component (reusable for create and edit)
  const renderForm = (isEdit = false) => (
    <POForm biayaAngkut={biayaAngkut} companies={companies} formData={formData} handleAddAddress={handleAddAddress} handleAddItem={handleAddItem} handleAddressChange={handleAddressChange} handleCreate={handleCreate} handleEdit={handleEdit} handleItemChange={handleItemChange} handleItemSelectProduct={handleItemSelectProduct} handleRemoveAddress={handleRemoveAddress} handleRemoveItem={handleRemoveItem} handleSupplierChange={handleSupplierChange} isEdit={isEdit} pembulatan={pembulatan} ppnAmount={ppnAmount} selectedPO={selectedPO} setFormData={setFormData} subtotal={subtotal} suppliers={suppliers} totalAmount={totalAmount} />
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="purchase-orders-page">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Purchase Orders</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchPOs} data-testid="refresh-po-button">
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            {['purchasing', 'admin'].includes(user?.role) && (
              <Button variant="outline" onClick={() => setShowBulkImport(true)} data-testid="bulk-import-po-button">
                <Download className="mr-2 h-4 w-4" /> Import Excel
              </Button>
            )}
            {['purchasing', 'admin'].includes(user?.role) && (
              <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button data-testid="create-po-button" className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="mr-2 h-4 w-4" /> Buat PO Baru
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Buat Purchase Order Baru</DialogTitle>
                    <p className="text-sm text-slate-600">Isi form untuk membuat PO baru</p>
                  </DialogHeader>
                  {renderForm(false)}
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <POBulkImportDialog open={showBulkImport} onOpenChange={setShowBulkImport} onSuccess={() => fetchPOs(1)} />

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Daftar Purchase Orders</CardTitle>
            <Input
              placeholder="Cari PO number, supplier, perusahaan..."
              value={poSearchFilter}
              onChange={(e) => setPoSearchFilter(e.target.value)}
              className="max-w-xs"
              data-testid="search-po"
            />
            {/* Export membawa filter & urutan yang sama dengan tabel (semua halaman). */}
            <ExportMenu table="purchase-orders" params={{
              search: debouncedSearch, sort_by: sortField, sort_dir: sortDirection,
              status_priority: sortField === 'status' && availableStatusesRef.current.length
                ? availableStatusesRef.current[statusPriorityIndex % availableStatusesRef.current.length] : undefined,
            }} />
            {user?.role === 'admin' && (
              <AdminBulkActions collectionType="purchase_orders" collectionLabel="Purchase Order" onRefresh={fetchPOs} />
            )}
          </CardHeader>
          <POTable SortIcon={SortIcon} canManagePO={canManagePO} currentPage={currentPage} fetchPOs={fetchPOs} handleRevise={handleRevise} handleSort={handleSort} handleSubmit={handleSubmit} openConfirmDialog={openConfirmDialog} openDeleteDialog={openDeleteDialog} openEdit={openEdit} openPartialDialog={openPartialDialog} pembulatan={pembulatan} pos={pos} selectedPO={selectedPO} setDpAmount={setDpAmount} setDpPO={setDpPO} setPartialItems={setPartialItems} setPrintPO={setPrintPO} setReasonTarget={setReasonTarget} setSelectedPO={setSelectedPO} setShowDPDialog={setShowDPDialog} setShowDetail={setShowDetail} setShowPrintOptions={setShowPrintOptions} setShowSubmitToAP={setShowSubmitToAP} setSubmitBiayaAngkut={setSubmitBiayaAngkut} setSubmitPembulatan={setSubmitPembulatan} setSubmitUsesVA={setSubmitUsesVA} setSubmitVAData={setSubmitVAData} setTanggalTerima={setTanggalTerima} sortField={sortField} totalCount={totalCount} totalPages={totalPages} user={user} />
        </Card>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDelete}
          title="Hapus Purchase Order"
          description="Apakah Anda yakin ingin menghapus PO ini? Tindakan ini tidak dapat dibatalkan dan akan dicatat dalam log penghapusan."
          itemName={itemToDelete ? `${itemToDelete.po_number} - ${itemToDelete.supplier_name} - Rp ${itemToDelete.total_amount?.toLocaleString('id-ID')}` : ''}
          loading={deleteLoading}
          cascadeChildren={cascadeChildren}
          cascadeLoading={cascadeLoading}
        />

        <ReasonDialog
          open={!!reasonTarget}
          onOpenChange={(v) => { if (!v) setReasonTarget(null); }}
          onConfirm={handleReasonConfirm}
          loading={reasonLoading}
          testId={reasonTarget?.type === 'close' ? 'close-po-dialog' : 'cancel-po-dialog'}
          title={reasonTarget?.type === 'close' ? 'Tutup PO Partial' : 'Batalkan PO'}
          description={reasonTarget?.type === 'close'
            ? 'Sisa qty yang belum diterima akan dicatat sebagai dibatalkan. Semua barang yang sudah diterima harus sudah ditagih.'
            : 'PO akan berstatus Dibatalkan dan tidak tampil lagi di daftar gudang/clerk. DP yang masih menunggu ikut dibatalkan.'}
          itemName={reasonTarget ? `${reasonTarget.po.po_number} - ${reasonTarget.po.supplier_name}` : ''}
          confirmLabel={reasonTarget?.type === 'close' ? 'Tutup PO' : 'Batalkan PO'}
          confirmClassName={reasonTarget?.type === 'close' ? 'bg-slate-700 hover:bg-slate-800' : 'bg-red-600 hover:bg-red-700'}
        />

        {/* Edit Dialog */}
        <Dialog open={showEdit} onOpenChange={(open) => { setShowEdit(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Purchase Order</DialogTitle>
              <p className="text-sm text-slate-600">Update data PO {selectedPO?.po_number}</p>
            </DialogHeader>
            {renderForm(true)}
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <PODetailDialog
          open={showDetail}
          onOpenChange={setShowDetail}
          po={selectedPO}
          onPrint={handlePrint}
        />

        <POConfirmDialog alasanReject={alasanReject} alasanTidakSesuai={alasanTidakSesuai} confirmType={confirmType} formData={formData} handleAddItem={handleAddItem} handleConfirmPO={handleConfirmPO} handleItemChange={handleItemChange} handleRemoveItem={handleRemoveItem} selectedPO={selectedPO} setAlasanReject={setAlasanReject} setAlasanTidakSesuai={setAlasanTidakSesuai} setShowConfirm={setShowConfirm} setTanggalTerima={setTanggalTerima} showConfirm={showConfirm} tanggalTerima={tanggalTerima} />

        <POPartialDialog allItemsReceived={allItemsReceived} handleConfirmPartial={handleConfirmPartial} handlePartialItemChange={handlePartialItemChange} hasAdjustments={hasAdjustments} partialItems={partialItems} selectedPO={selectedPO} setShowPartial={setShowPartial} setTanggalTerima={setTanggalTerima} showPartial={showPartial} tanggalTerima={tanggalTerima} />

        {/* Submit to AP Dialog */}
        <POSubmitToAPDialog
          open={showSubmitToAP}
          onOpenChange={setShowSubmitToAP}
          selectedPO={selectedPO}
          invoiceNumber={invoiceNumber}
          setInvoiceNumber={setInvoiceNumber}
          tanggalInvoice={tanggalInvoice}
          setTanggalInvoice={setTanggalInvoice}
          tanggalTerima={tanggalTerima}
          setTanggalTerima={setTanggalTerima}
          partialItems={partialItems}
          handlePartialItemChange={handlePartialItemChange}
          handleSubmitToAP={handleSubmitToAP}
          submitUsesVA={submitUsesVA}
          setSubmitUsesVA={setSubmitUsesVA}
          submitVAData={submitVAData}
          setSubmitVAData={setSubmitVAData}
          submitPembulatan={submitPembulatan}
          setSubmitPembulatan={setSubmitPembulatan}
          submitBiayaAngkut={submitBiayaAngkut}
          setSubmitBiayaAngkut={setSubmitBiayaAngkut}
        />

        <PODownPaymentDialog dpAmount={dpAmount} dpLoading={dpLoading} dpPO={dpPO} handleCreateDP={handleCreateDP} setDpAmount={setDpAmount} setShowDPDialog={setShowDPDialog} showDPDialog={showDPDialog} />

        <POPrintOptionsDialog handlePrint={handlePrint} printPO={printPO} printTandaTerima={printTandaTerima} setShowPrintOptions={setShowPrintOptions} showPrintOptions={showPrintOptions} user={user} />
      </div>
    </Layout>
  );
}



